/**
 * Consumer Identity Step - Simplified
 * 
 * Confirms or completes missing mailing details for dispute letters.
 * Pre-fills from user profile data - only asks for missing or required info.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  User,
  MapPin,
  Shield,
  CheckCircle2,
  Info,
  Edit2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';

const ConsumerIdentityStep = ({
  onContinue,
  onBack,
  initialData,
  reportData,
}) => {
  const { user } = useAuth();
  
  // Get profile data - pre-fill from user account
  const profileFirstName = user?.first_name || user?.firstName || '';
  const profileLastName = user?.last_name || user?.lastName || '';
  const profileFullName = user?.full_name || user?.name || `${profileFirstName} ${profileLastName}`.trim();
  
  // Get any saved dispute identity data
  const savedIdentity = user?.dispute_identity || {};
  
  // Safely handle props
  const safeInitialData = initialData || {};
  const safeReportData = reportData || {};
  const reportConsumer = safeReportData.consumer || {};
  const reportAddress = reportConsumer.address || {};
  
  // Initialize form with priority: initialData > savedIdentity > user profile > report data
  const [formData, setFormData] = useState({
    // Name from profile (pre-filled, not editable here)
    firstName: profileFirstName || safeInitialData.firstName || savedIdentity.first_name || reportConsumer.first_name || '',
    middleName: safeInitialData.middleName || savedIdentity.middle_name || reportConsumer.middle_name || '',
    lastName: profileLastName || safeInitialData.lastName || savedIdentity.last_name || reportConsumer.last_name || '',
    suffix: safeInitialData.suffix || savedIdentity.suffix || '',
    
    // SSN - pre-fill if available, editable
    ssn: safeInitialData.ssn || savedIdentity.ssn_last4 || '',
    
    // DOB - pre-fill if available
    dateOfBirth: safeInitialData.dateOfBirth || savedIdentity.date_of_birth || reportConsumer.dob || '',
    
    // Address - pre-fill if available, editable
    currentAddress: {
      street: safeInitialData.currentAddress?.street || savedIdentity.address?.street || reportAddress.street || '',
      city: safeInitialData.currentAddress?.city || savedIdentity.address?.city || reportAddress.city || '',
      state: safeInitialData.currentAddress?.state || savedIdentity.address?.state || reportAddress.state || '',
      zipCode: safeInitialData.currentAddress?.zipCode || savedIdentity.address?.zip || reportAddress.zip || '',
    },
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [editingName, setEditingName] = useState(false);

  // Update form data when user or savedIdentity changes
  useEffect(() => {
    const newSavedIdentity = user?.dispute_identity || {};
    const newAddress = newSavedIdentity.address || {};
    
    setFormData(prev => ({
      firstName: profileFirstName || prev.firstName || newSavedIdentity.first_name || '',
      middleName: prev.middleName || newSavedIdentity.middle_name || '',
      lastName: profileLastName || prev.lastName || newSavedIdentity.last_name || '',
      suffix: prev.suffix || newSavedIdentity.suffix || '',
      ssn: prev.ssn || newSavedIdentity.ssn_last4 || '',
      dateOfBirth: prev.dateOfBirth || newSavedIdentity.date_of_birth || '',
      currentAddress: {
        street: prev.currentAddress?.street || newAddress.street || '',
        city: prev.currentAddress?.city || newAddress.city || '',
        state: prev.currentAddress?.state || newAddress.state || '',
        zipCode: prev.currentAddress?.zipCode || newAddress.zip || '',
      },
    }));
  }, [user, profileFirstName, profileLastName]);

  // Check if we have a complete name from profile
  const hasProfileName = profileFirstName && profileLastName;
  
  // Check which fields are missing/need completion
  const needsAddress = !formData.currentAddress.street || !formData.currentAddress.city || 
                       !formData.currentAddress.state || !formData.currentAddress.zipCode;

  // Validation for required fields
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return !value?.trim() ? 'First name is required' : null;
      case 'lastName':
        return !value?.trim() ? 'Last name is required' : null;
      case 'currentAddress.street':
        return !value?.trim() ? 'Street address is required' : null;
      case 'currentAddress.city':
        return !value?.trim() ? 'City is required' : null;
      case 'currentAddress.state':
        return !value?.trim() ? 'State is required' : null;
      case 'currentAddress.zipCode':
        return !value?.trim() ? 'ZIP code is required' : 
               !/^\d{5}(-\d{4})?$/.test(value) ? 'Invalid ZIP code' : null;
      default:
        return null;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate name if not pre-filled from profile
    if (!hasProfileName || editingName) {
      if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    }
    
    // Always validate address
    if (!formData.currentAddress.street?.trim()) newErrors['currentAddress.street'] = 'Street address is required';
    if (!formData.currentAddress.city?.trim()) newErrors['currentAddress.city'] = 'City is required';
    if (!formData.currentAddress.state?.trim()) newErrors['currentAddress.state'] = 'State is required';
    if (!formData.currentAddress.zipCode?.trim()) {
      newErrors['currentAddress.zipCode'] = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.currentAddress.zipCode)) {
      newErrors['currentAddress.zipCode'] = 'Invalid ZIP code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let value;
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      value = formData[parent]?.[child];
    } else {
      value = formData[field];
    }
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Use profile name if not editing, otherwise use form data
      const finalData = {
        ...formData,
        firstName: (hasProfileName && !editingName) ? profileFirstName : formData.firstName,
        lastName: (hasProfileName && !editingName) ? profileLastName : formData.lastName,
      };
      onContinue(finalData);
    } else {
      setTouched({
        firstName: true,
        lastName: true,
        'currentAddress.street': true,
        'currentAddress.city': true,
        'currentAddress.state': true,
        'currentAddress.zipCode': true,
      });
    }
  };

  const isFormValid = () => {
    const hasName = (hasProfileName && !editingName) || (formData.firstName?.trim() && formData.lastName?.trim());
    const hasAddress = formData.currentAddress?.street?.trim() &&
                       formData.currentAddress?.city?.trim() &&
                       formData.currentAddress?.state?.trim() &&
                       formData.currentAddress?.zipCode?.trim();
    return hasName && hasAddress;
  };

  // Get display name for preview
  const displayName = () => {
    if (hasProfileName && !editingName) {
      return [profileFirstName, formData.middleName, profileLastName, formData.suffix].filter(Boolean).join(' ');
    }
    return [formData.firstName, formData.middleName, formData.lastName, formData.suffix].filter(Boolean).join(' ');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 py-8 px-4"
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Confirm Your Mailing Details
          </h1>
          <p className="text-slate-400">
            We prefilled your dispute letter header from your account information
          </p>
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-emerald-300 text-sm">
                Please confirm or complete any missing mailing details below. 
                The bureaus require your name and address to process your dispute.
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-stone-900/50 border border-stone-800 rounded-2xl p-6 space-y-6">
          
          {/* Name Section - Pre-filled from profile */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-400" />
                Your Name
              </h3>
              {hasProfileName && !editingName && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingName(true)}
                  className="text-slate-400 hover:text-white text-xs"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {hasProfileName && !editingName ? (
              // Show pre-filled name as confirmation
              <div className="bg-stone-800/50 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-white font-medium">{profileFullName}</span>
                  <Badge className="bg-emerald-500/10 text-emerald-400 text-xs">From your account</Badge>
                </div>
                {formData.middleName && (
                  <p className="text-slate-400 text-sm mt-1 ml-6">
                    Middle name: {formData.middleName}
                  </p>
                )}
              </div>
            ) : (
              // Editable name fields
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm">First Name *</Label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    onBlur={() => handleBlur('firstName')}
                    placeholder="John"
                    className={`mt-1 bg-stone-800/50 border-slate-700 text-white ${
                      touched.firstName && errors.firstName ? 'border-red-500' : ''
                    }`}
                    data-testid="consumer-first-name"
                  />
                  {touched.firstName && errors.firstName && (
                    <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Last Name *</Label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    onBlur={() => handleBlur('lastName')}
                    placeholder="Smith"
                    className={`mt-1 bg-stone-800/50 border-slate-700 text-white ${
                      touched.lastName && errors.lastName ? 'border-red-500' : ''
                    }`}
                    data-testid="consumer-last-name"
                  />
                  {touched.lastName && errors.lastName && (
                    <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Middle Name</Label>
                  <Input
                    value={formData.middleName}
                    onChange={(e) => handleInputChange('middleName', e.target.value)}
                    placeholder="Michael"
                    className="mt-1 bg-stone-800/50 border-slate-700 text-white"
                    data-testid="consumer-middle-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Suffix</Label>
                  <Input
                    value={formData.suffix}
                    onChange={(e) => handleInputChange('suffix', e.target.value)}
                    placeholder="Jr., Sr., III"
                    className="mt-1 bg-stone-800/50 border-slate-700 text-white"
                    data-testid="consumer-suffix"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Current Mailing Address Section */}
          <div>
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-400" />
              Current Mailing Address
              {needsAddress && (
                <Badge className="bg-amber-500/10 text-amber-400 text-xs ml-2">Required</Badge>
              )}
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 text-sm">Street Address *</Label>
                <Input
                  value={formData.currentAddress.street}
                  onChange={(e) => handleInputChange('currentAddress.street', e.target.value)}
                  onBlur={() => handleBlur('currentAddress.street')}
                  placeholder="123 Main Street, Apt 4B"
                  className={`mt-1 bg-stone-800/50 border-slate-700 text-white ${
                    touched['currentAddress.street'] && errors['currentAddress.street'] ? 'border-red-500' : ''
                  }`}
                  data-testid="consumer-street"
                />
                {touched['currentAddress.street'] && errors['currentAddress.street'] && (
                  <p className="text-red-400 text-xs mt-1">{errors['currentAddress.street']}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm">City *</Label>
                  <Input
                    value={formData.currentAddress.city}
                    onChange={(e) => handleInputChange('currentAddress.city', e.target.value)}
                    onBlur={() => handleBlur('currentAddress.city')}
                    placeholder="New York"
                    className={`mt-1 bg-stone-800/50 border-slate-700 text-white ${
                      touched['currentAddress.city'] && errors['currentAddress.city'] ? 'border-red-500' : ''
                    }`}
                    data-testid="consumer-city"
                  />
                  {touched['currentAddress.city'] && errors['currentAddress.city'] && (
                    <p className="text-red-400 text-xs mt-1">{errors['currentAddress.city']}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">State *</Label>
                  <Input
                    value={formData.currentAddress.state}
                    onChange={(e) => handleInputChange('currentAddress.state', e.target.value.toUpperCase().slice(0, 2))}
                    onBlur={() => handleBlur('currentAddress.state')}
                    placeholder="NY"
                    maxLength={2}
                    className={`mt-1 bg-stone-800/50 border-slate-700 text-white ${
                      touched['currentAddress.state'] && errors['currentAddress.state'] ? 'border-red-500' : ''
                    }`}
                    data-testid="consumer-state"
                  />
                  {touched['currentAddress.state'] && errors['currentAddress.state'] && (
                    <p className="text-red-400 text-xs mt-1">{errors['currentAddress.state']}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">ZIP Code *</Label>
                  <Input
                    value={formData.currentAddress.zipCode}
                    onChange={(e) => handleInputChange('currentAddress.zipCode', e.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
                    onBlur={() => handleBlur('currentAddress.zipCode')}
                    placeholder="10001"
                    className={`mt-1 bg-stone-800/50 border-slate-700 text-white ${
                      touched['currentAddress.zipCode'] && errors['currentAddress.zipCode'] ? 'border-red-500' : ''
                    }`}
                    data-testid="consumer-zip"
                  />
                  {touched['currentAddress.zipCode'] && errors['currentAddress.zipCode'] && (
                    <p className="text-red-400 text-xs mt-1">{errors['currentAddress.zipCode']}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Optional Identity Information */}
          <div>
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-400" />
              Identity Verification
              <Badge className="bg-slate-700 text-slate-300 text-xs ml-2">Optional</Badge>
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Adding these helps bureaus match your dispute to your credit file more accurately.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Last 4 of SSN</Label>
                <Input
                  value={formData.ssn}
                  onChange={(e) => handleInputChange('ssn', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="1234"
                  maxLength={4}
                  className="mt-1 bg-stone-800/50 border-slate-700 text-white"
                  data-testid="consumer-ssn"
                />
                <p className="text-slate-500 text-xs mt-1">Will appear as ***-**-{formData.ssn || '____'}</p>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className="mt-1 bg-stone-800/50 border-slate-700 text-white"
                  data-testid="consumer-dob"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Letter Header Preview */}
        <div className="mt-6 p-4 bg-stone-900/50 border border-stone-800 rounded-xl">
          <p className="text-slate-400 text-xs mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
            Your dispute letter header will appear as:
          </p>
          <div className="bg-white/5 p-4 rounded-lg font-mono text-sm text-white">
            {displayName() ? (
              <>
                <p className="font-semibold">{displayName().toUpperCase()}</p>
                {formData.currentAddress.street && <p>{formData.currentAddress.street}</p>}
                <p>
                  {[
                    formData.currentAddress.city,
                    formData.currentAddress.state,
                    formData.currentAddress.zipCode
                  ].filter(Boolean).join(', ') || 'City, State ZIP'}
                </p>
                {formData.ssn && <p className="text-slate-400">SSN (last 4): ***-**-{formData.ssn}</p>}
                {formData.dateOfBirth && <p className="text-slate-400">DOB: {formData.dateOfBirth}</p>}
              </>
            ) : (
              <p className="text-slate-500 italic">Complete the required fields to see preview</p>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-slate-400 hover:text-white"
            data-testid="consumer-identity-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!isFormValid()}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
            data-testid="consumer-identity-continue"
          >
            Continue to Review
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ConsumerIdentityStep;
